import { mapState, mapActions, mapGetters } from 'vuex'
import {  token_expired } from '@old/utils/helpers/token_helper.js'
import { refreshToken } from '@/composables/useAuth'

export const api_mixin = {
    computed: {
        ...mapState(['public_token']),
        ...mapGetters(['token'])
    },
    methods: {
        async api_get(payload) {
            const url = this.api_url_and_params(payload)
            const response = await fetch(url, {
                headers: {
                    'X-API-Key': payload.token || (await this.get_valid_token(payload)),
                    'Cache-Control': 'no-cache',
                },
            })
            response['data'] = await response.json()
            if (!response.ok) {
                throw response
            }
            return response
        },
        async api_post(payload) {
            const url = this.api_url_and_params(payload)

            const headers = {
                    'X-API-Key': payload.token || (await this.get_valid_token(payload)),
            }
            const json = !(payload.data instanceof FormData)
            if (json) {
                headers['Content-Type'] = 'application/json'
            }

            const response = await fetch(url, {
                method: 'POST',
                body: json ? JSON.stringify(payload.data) : payload.data,
                headers: headers,
            })
            response['data'] = await response.json()
            if (!response.ok) {
                throw response
            }
            return response
        },
        async api_patch(payload) {
            const url = this.api_url_and_params(payload)
            const response = await fetch(url, {
                method: 'PATCH',
                body: JSON.stringify(payload.data),
                headers: {
                    'X-API-Key': payload.token || (await this.get_valid_token(payload)),
                    'Content-Type': 'application/json',
                    'If-Match': payload['if_match'],
                },
            })
            response['data'] = await response.json()
            if (!response.ok) {
                throw response
            }
            return response
        },
        async api_delete(payload) {
            const url = this.api_url_and_params(payload)
            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'X-API-Key': payload.token || (await this.get_valid_token(payload)),
                    'If-Match': payload['if_match'],
                },
            })
            if (!response.ok) {
                response['data'] = await response.json()
                throw response
            }
            return response
        },
        async api_get_all_items(payload) {
            const items = []

            let page = 1
            let total = undefined
            let continue_to_fetch = true

            while (continue_to_fetch) {
                if (!payload.url.startsWith('/')) {
                    payload.url = `/${payload.url}`
                }

                const request = await this.api_get({
                    url: payload.url,
                    params: { ...payload.params, page },
                })
                if (request.status === 200) {
                    if (total === undefined) total = request.data['_meta']['total']
                    const data = request.data['_items']
                    items.push(...data)
                } else {
                    continue
                }

                if (items.length >= total) {
                    continue_to_fetch = false
                } else {
                    page += 1
                }
            }

            return items
        },
        async get_valid_token(payload) {
            if (
                !token_expired(this.token) ||
                payload.url.includes('authenticates') ||
                payload.url.includes('public')
            ) {
                return this.token
            }
            // ADA-4410: single refresh implementation (useAuth.refreshToken, Web
            // Locks-guarded) replaces the token_has_expired flag + 20s polling.
            // The bridge getter points at authStore, so this.token is fresh after.
            await refreshToken()
            return this.token
        },
        async get_public_token() {
            if (!this.public_token || token_expired(this.public_token)) {
                const response = await this.api_post({
                    url: `/authenticates/public-user/${import.meta.env.VITE_APP_NAME}`,
                })
                if (response.status === 200) {
                    await this.set_state_property({
                        property: 'public_token',
                        data: response.data.token,
                    })
                }
            }
        },
        async sleep(ms) {
            return new Promise((resolve) => setTimeout(resolve, ms))
        },
        api_url_and_params(payload) {
            const params = new URLSearchParams()

            if (!payload.url.startsWith('/')) {
                payload.url = `/${payload.url}`
            }
            for (const key in payload.params) {
                if (typeof payload.params[key] === 'object' && payload.params[key] !== null) {
                    params.append(key, JSON.stringify(payload.params[key]))
                } else {
                    params.append(key, payload.params[key])
                }
            }
            if (params.size) {
                return `${import.meta.env.VITE_APP_API_ROOT}${payload.url}?${params}`
            } else {
                return `${import.meta.env.VITE_APP_API_ROOT}${payload.url}`
            }
        },
        ...mapActions(['set_state_property']),
    },
}
